import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Col, DatePicker, Input, Row, Select, Spin, Table, Tag } from 'antd';
import { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateField, updatePropsScheduled } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setScheduledMentenancePlannings } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';

import CompanyService from '../../../../services/company/CompanyService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import UserService from '../../../../services/user/UserService';
import { RootState } from '../../../../store';
import MentenancePlanningModal from '../MentenancePlanningModal/MentenancePlanningModal';

const MentenancePlanningScheduledTab: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const reduxMentenancePlanning = useSelector((state: RootState) => state?.models?.mentenancePlanning);
  const scheduledMentenancePlannings = useSelector(
    (state: RootState) => state?.store?.mentenancePlanning?.scheduledMentenancePlanningData ?? [],
  );

  const searchTerm = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsScheduled?.searchTerm);
  const skip = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsScheduled?.skip);
  const take = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsScheduled?.take);
  const refresh = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsScheduled?.refresh);
  const selectedTechnician = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsScheduled?.selectedTechnician,
  );

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage =
    Math.floor(reduxMentenancePlanning?.propsScheduled?.skip / reduxMentenancePlanning?.propsScheduled?.take) + 1;
  const pageSizes = [10, 20, 30, 50];

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { message } = App.useApp();

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updatePropsScheduled({ field: 'searchTerm', value: termInUrl }));
    dispatch(updatePropsScheduled({ field: 'skip', value: calculatedSkip }));
    dispatch(updatePropsScheduled({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isMentenancePlanningLoading,
    refetch: refetch,
  } = useQuery({
    queryKey: [
      'mentenancePlannings',
      searchTerm,
      skip,
      take,
      refresh,
      selectedTechnician,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      dateRange,
    ],
    queryFn: () =>
      MentenancePlanningService.searchScheduled({
        statusIds: [2, 3],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        termen: searchTerm || undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
        skip,
        take,
        assignedTechnicianId: selectedTechnician?.id,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      }).then((data) => {
        dispatch(updatePropsScheduled({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setScheduledMentenancePlannings(data?.response));
        dispatch(updatePropsScheduled({ field: 'refresh', value: false }));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician , senior_tehnician',
      }).then((data) => {
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsScheduled({ field: 'searchTerm', value }));
      dispatch(updatePropsScheduled({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch],
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [searchTerm],
  );

  const handleViewRecord = useCallback(
    (record: any) => {
      setSelectedRecord(record);
      setIsModalVisible(true);
    },
    [reduxMentenancePlanning],
  );

  const handleDeleteRecord = useCallback(
    async (record: any) => {
      try {
        const response = await MentenancePlanningService.delete(record.id);
        if (response?.errors?.length) {
          // Handle errors if any
          console.error('Failed to delete record:', response.errors);
        } else {
          // Refresh the data after deletion
          message.success(`Mentenanta ${record.number} a fost stearsa`);
          dispatch(updatePropsScheduled({ field: 'refresh', value: true }));
        }
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    },
    [reduxMentenancePlanning],
  );

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  }, [reduxMentenancePlanning]);

  const handleTechnicianChange = useCallback(
    (value: string | null) => {
      dispatch(
        updatePropsScheduled({
          field: 'selectedTechnician',
          value: value ? _users.find((u: any) => u.id == value) : null, // Ensure it sets to `null`
        }),
      );
    },
    [selectedTechnician],
  );

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates || [null, null]);
    dispatch(updatePropsScheduled({ field: 'skip', value: 0 }));
  };

  const baseColumns: any = useMemo(
    () => [
      { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
      {
        title: 'User',
        dataIndex: '',
        render: (record: any) => (
          <div>
            <div>{record?.registeredByUser?.name}</div>
            <div>{record.createDate ? moment(record.createDate).format('DD-MM-YYYY') : '-'}</div>
          </div>
        ),
      },
      {
        title: 'Beneficiar',
        key: 'company',
        render: (record: any) => {
          const company = record?.metadata?.company ?? record?.company;
          return <div>{company?.name}</div>;
        },
      },
      {
        title: 'Punct',
        dataIndex: '',
        render: (record: any) => {
          return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
        },
      },
      {
        title: 'Sistem',
        dataIndex: 'mentenancePlanningSystemTypes',
        key: 'mentenancePlanningSystemTypes',
        render: (mentenancePlanningSystemTypes: any[]) => (
          <>
            {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
              <span>
                {mentenancePlanningSystemTypes.length > 0
                  ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                  : ''}
              </span>
            )}
          </>
        ),
      },
      {
        title: 'Perioada programata',
        dataIndex: '',
        render: (_value: any, record: any, _index: any) => {
          return (
            <>
              <span>{record.appointment ? moment(record.appointment).format('DD-MM-YYYY') : '-'}</span>
            </>
          );
        },
      },
      {
        title: 'Numar Contract',
        dataIndex: 'contract',
        render: (contract: any) => {
          return <>{contract?.number}</>;
        },
      },
      { title: 'Status', dataIndex: ['status', 'name'] },
      { title: 'Tehnician', dataIndex: ['assignedTechnician', 'name'] },
      {
        title: 'Actiuni',
        dataIndex: 'actions',
        key: 'actions',
        render: (_text: any, record: any) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '80px' }}>
              <Button onClick={() => handleViewRecord(record)} icon={<EyeOutlined />}></Button>
              <Button onClick={() => handleDeleteRecord(record)} icon={<DeleteOutlined />}></Button>
            </div>
          );
        },
      },
    ],
    [scheduledMentenancePlannings],
  );

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxMentenancePlanning?.propsScheduled.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updatePropsScheduled({ field: 'skip', value: skipValue }));
      dispatch(updatePropsScheduled({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updatePropsScheduled({ field: 'skip', value: 0 }));
      dispatch(updatePropsScheduled({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  if (isMentenancePlanningLoading || isUsersLoading || isCompaniesLoading || isSystemTypesLoading) {
    return <Spin className='spinner' size='large'></Spin>;
  }
  return (
    <div className='table-page-no-h-scroll'>
      <h3>Mentenante Programate</h3>

      <Row gutter={16} style={{ marginBottom: '16px', rowGap: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder='Cauta...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: '100%' }}
            allowClear
            autoFocus={true}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            value={selectedTechnician?.name}
            showSearch
            allowClear
            placeholder='Filtreaza dupa Tehnician'
            onChange={handleTechnicianChange}
            style={{ width: '100%' }}
            optionFilterProp='children'
          >
            {_users?.map((user: any) => (
              <Select.Option key={user.id} value={user.id}>
                {user.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces?.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes?.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <DatePicker.RangePicker
            value={[dateRange[0], dateRange[1]]}
            onChange={handleDateRangeChange}
            placeholder={['Data inceput', 'Data sfarsit']}
            allowClear
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <div className='table-scroll-wrapper'>
        <Table
          columns={baseColumns}
          dataSource={scheduledMentenancePlannings}
          pagination={paginationConfig}
          rowKey='number'
          scroll={{ x: 'max-content' }}
        />
      </div>

      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
      />
    </div>
  );
};

export default MentenancePlanningScheduledTab;
